import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';
import debounce from 'lodash.debounce';
import { EBaseOrderDirection } from 'common/models/requestModels';
import { EStoreScopeType } from 'entities/Permission/Permission.models';
import { IGroupModel, IRegionModel } from 'entities/Region/Region.models';
import { regionTransport } from 'entities/Region/Region.transport';
import { IStoreModel } from 'entities/Store/Store.models';
import { storeTransport } from 'entities/Store/Store.transport';

interface IComponentProps {
  type?: EStoreScopeType;
  value?: string[];
  onChange?: (ids: string[]) => void;
  disabled?: boolean;
}

interface IOption {
  value: string;
  label: string;
}

// BE max page size; also the scroll step for lazy-loading the next batch of stores.
const PAGE_SIZE = 100;

// Debounce for the server-side store search triggered from the search box.
const SEARCH_DEBOUNCE = 300;

const mapStore = (s: IStoreModel): IOption => ({ value: s.id, label: s.storeName || s.storeCode || s.id });

// Append options whose value isn't already present, keeping `base` order.
const appendUnique = (base: IOption[], extra: IOption[]): IOption[] => {
  const known = new Set(base.map((o) => o.value));
  const missing = extra.filter((o) => !known.has(o.value));

  return missing.length ? [...base, ...missing] : base;
};

// Ids multi-select for a franchisor user's store scope. Options depend on `type` (region/group/store),
// each fetched on demand (limit 100 = BE max). The stores dropdown lazy-loads further pages on scroll
// and queries the BE on search input (typing in the search box) so a store from a not-yet-loaded page
// is still findable; region/group lists stay client-filtered.
// Split from the type radio so each can sit in its own Form.Item and get its own validation error;
// renders nothing for "All", which carries no ids.
export const StoreScopeIdsSelector: React.FC<IComponentProps> = ({ type, value, onChange, disabled }) => {
  const [regions, setRegions] = useState<IOption[]>([]);
  const [groups, setGroups] = useState<IOption[]>([]);
  const [stores, setStores] = useState<IOption[]>([]);
  // Resolved labels for already-selected ids, kept apart from the paged/searched list so a selected
  // store outside the current result set still renders as a name instead of a raw uuid.
  const [selectedStores, setSelectedStores] = useState<IOption[]>([]);
  const [offset, setOffset] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const loadingRef = useRef(false);
  // Only the latest page/search request may commit its result (guards against out-of-order responses).
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (type !== EStoreScopeType.Region) {
      return;
    }

    let active = true;

    regionTransport
      .getRegionCollection({ limit: 100, orderDirection: EBaseOrderDirection.ASC })
      .then((c) => active && setRegions(c.data.map((r: IRegionModel) => ({ value: r.id, label: r.name }))))
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [type]);

  useEffect(() => {
    if (type !== EStoreScopeType.Group) {
      return;
    }

    let active = true;

    // Groups span all regions here (scope can mix regions), so we fetch the full list (no regionId
    // filter) and label each with its region for disambiguation.
    regionTransport
      .getGroupCollection({ limit: 100, orderDirection: EBaseOrderDirection.ASC })
      .then(
        (c) =>
          active &&
          setGroups(
            c.data.map((g: IGroupModel) => ({ value: g.id, label: g.region ? `${g.region.name} / ${g.name}` : g.name }))
          )
      )
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [type]);

  // Resolve labels for the currently selected ids (one request, regardless of paging/search state).
  const selectedKey = value?.join(',') ?? '';

  useEffect(() => {
    if (type !== EStoreScopeType.Stores || !value?.length) {
      setSelectedStores([]);

      return;
    }

    let active = true;

    storeTransport
      .getCollection({ ids: value, limit: value.length })
      .then((c) => active && setSelectedStores(c.data.map(mapStore)))
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [type, selectedKey]);

  // Fetch a page of stores: `append` adds the next page (scroll), otherwise it replaces the list
  // (initial load / new search term).
  const fetchStores = (searchTerm: string, nextOffset: number, append: boolean): void => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    loadingRef.current = true;
    setLoading(true);

    storeTransport
      .getCollection({ limit: PAGE_SIZE, offset: nextOffset, search: searchTerm || undefined })
      .then((c) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        const page = c.data.map(mapStore);

        setStores((prev) => (append ? appendUnique(prev, page) : page));
        setOffset(nextOffset);
        setTotal(c.meta.count);
      })
      .catch(() => undefined)
      .finally(() => {
        if (requestId === requestIdRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    if (type !== EStoreScopeType.Stores) {
      return;
    }

    setSearch('');
    fetchStores('', 0, false);
  }, [type]);

  const debouncedFetch = useMemo(() => debounce((term: string) => fetchStores(term, 0, false), SEARCH_DEBOUNCE), []);

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  const handleSearch = (term: string) => {
    if (type !== EStoreScopeType.Stores) {
      return;
    }

    const normalized = term.trim();

    if (normalized === search) {
      return;
    }

    setSearch(normalized);
    debouncedFetch(normalized);
  };

  const handleChange = (ids: string[]) => {
    onChange?.(ids);

    // antd clears the search box after a pick (autoClearSearchValue); reset the list back to the
    // first page so the other stores are visible again instead of just the filtered result.
    if (type === EStoreScopeType.Stores && search) {
      debouncedFetch.cancel();
      setSearch('');
      fetchStores('', 0, false);
    }
  };

  // Lazy-load the next page of stores (keeping the current search term) when the dropdown is
  // scrolled near the bottom.
  const handleStoresPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (loadingRef.current || stores.length >= total || type !== EStoreScopeType.Stores) {
      return;
    }

    const target = e.currentTarget;

    if (target.scrollTop + target.offsetHeight < target.scrollHeight - 32) {
      return;
    }

    fetchStores(search, offset + PAGE_SIZE, true);
  };

  const storeOptions = useMemo(() => appendUnique(stores, selectedStores), [stores, selectedStores]);

  if (!type || type === EStoreScopeType.All) {
    return null;
  }

  const options = type === EStoreScopeType.Region ? regions : type === EStoreScopeType.Group ? groups : storeOptions;
  const placeholder =
    type === EStoreScopeType.Region
      ? 'Select regions'
      : type === EStoreScopeType.Group
      ? 'Select groups'
      : 'Select stores';

  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      className="width-full"
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      loading={loading}
      options={options}
      optionFilterProp="label"
      // Stores are filtered server-side (see handleSearch); region/group stay client-filtered.
      filterOption={type !== EStoreScopeType.Stores}
      onSearch={handleSearch}
      onChange={handleChange}
      onPopupScroll={handleStoresPopupScroll}
      getPopupContainer={(triggerNode) => triggerNode as HTMLElement}
    />
  );
};
